import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactElement } from 'react';
import { AuthProvider } from './auth/AuthProvider';
import { useAuth } from './auth/useAuth';
import { isSupabaseConfigured } from './lib/env';
import { Screen, Spinner } from './components';
import { AuthPage } from './pages/AuthPage';
import { JoinTripPage } from './pages/JoinTripPage';
import { NewTripPage } from './pages/NewTripPage';
import { SetupRequiredPage } from './pages/SetupRequiredPage';
import { TripsPage } from './pages/TripsPage';
import { TripPage } from './pages/trip/TripPage';
import { ExpensesTab } from './pages/trip/ExpensesTab';
import { GroupTab } from './pages/trip/GroupTab';
import { OverviewTab } from './pages/trip/OverviewTab';
import { SettleTab } from './pages/trip/SettleTab';

function FullScreenSpinner() {
  return (
    <Screen center>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner size={28} label="Checking your session" />
      </div>
    </Screen>
  );
}

function RequireAuth({ children }: { children: ReactElement }) {
  const { session, initialising } = useAuth();

  // Rendering the redirect before the session lookup settles would bounce a
  // signed-in user to the auth screen on every refresh.
  if (initialising) return <FullScreenSpinner />;
  if (!session) return <Navigate to="/signin" replace />;
  return children;
}

function RedirectIfSignedIn({ children }: { children: ReactElement }) {
  const { session, initialising } = useAuth();
  if (initialising) return <FullScreenSpinner />;
  if (session) return <Navigate to="/" replace />;
  return children;
}

function Router() {
  return (
    <Routes>
      <Route
        path="/signin"
        element={
          <RedirectIfSignedIn>
            <AuthPage />
          </RedirectIfSignedIn>
        }
      />

      <Route
        path="/"
        element={
          <RequireAuth>
            <TripsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/trips/new"
        element={
          <RequireAuth>
            <NewTripPage />
          </RequireAuth>
        }
      />
      <Route
        path="/join"
        element={
          <RequireAuth>
            <JoinTripPage />
          </RequireAuth>
        }
      />

      {/*
        The trip's tabs are nested routes rather than component state so that
        the back button and a deep link both work — this is installed to a home
        screen, where the browser's back gesture is the only navigation
        affordance the user has.
      */}
      <Route
        path="/trips/:tripId"
        element={
          <RequireAuth>
            <TripPage />
          </RequireAuth>
        }
      >
        <Route index element={<OverviewTab />} />
        <Route path="expenses" element={<ExpensesTab />} />
        <Route path="settle" element={<SettleTab />} />
        <Route path="group" element={<GroupTab />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // Short-circuit before AuthProvider so an unconfigured app never attempts a
  // network call, and the user gets instructions rather than a white screen.
  if (!isSupabaseConfigured) return <SetupRequiredPage />;

  return (
    <AuthProvider>
      <Router />
    </AuthProvider>
  );
}
